import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActors,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import { useHelper3D } from "../../hooks/HelperHooks3D";
import { Actor } from "../../models/Actor";
import InteractionPointPreview, {
  ActorPointStrategy,
  InteractionPointPreviewProps,
  NpcPointStrategy,
  PlayerPointStrategy,
} from "./InteractionPointPreview";
import { NpcPopoverButton } from "./NpcPopover";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LinkIcon from "@mui/icons-material/Link";
import type { SequenceActorPlacement } from "./useSequenceStateOnMap";
import { sequenceUncertainClassName, sequenceStateDot, sequenceStateDotClassName } from "./sequenceStateDot";
import classNames from "classnames";
import NpcPointPreviewPopover from "./NpcPointPreviewPopover";
import useViewport3D from "./Viewport3D.hook";
import { RgbaColor } from "../../app/slices/EnvironmentSlice";
import { Vec2D } from "../../models/Vec2D";

interface NpcPointPreviewProps extends InteractionPointPreviewProps {
  positionId: string;
  /**
   * Who the sequence state on the map has here (null: nobody). Shown read-only:
   * the popover edits the take, not the sequence. Undefined shows the take.
   */
  sequencePlacement?: SequenceActorPlacement | null;
  /**
   * The take's own attachments, while the map shows the take: an actor holding
   * a prop is drawn by the attachment marker on top; an actor on a prop has
   * its own spot dimmed (the game puts it on the prop).
   */
  takeAttachments?: { holderIds: Set<string>; riderIds: Set<string> };
  takeColor?: RgbaColor;
  positionDirection?: Vec2D;
}

export default function NpcPointPreview(props: NpcPointPreviewProps) {
  const dispatch = useDispatch();
  const { x, y, positionId, disabled, takeColor, positionDirection } = {
    ...props,
  };
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const { openQuickActorModal } = useViewport3D();
  const {
    getActorAtPositionIdInSelectedTake,
    addActorToSelectedTake,
    removeActorFromSelectedTake,
  } = useHelper3D();

  const take = useSelector(selectSelectedTake3D);
  const actors = useSelector(selectActors);
  const { sequencePlacement } = props;
  const isSequenceState = sequencePlacement !== undefined;
  const myActor = useMemo(
    () =>
      isSequenceState
        ? actors.find((actor) => actor.id === sequencePlacement?.actorId)
        : getActorAtPositionIdInSelectedTake(positionId),
    [actors, getActorAtPositionIdInSelectedTake, isSequenceState, positionId, sequencePlacement]
  );
  // In the take, on a prop (Player to Prop): the game puts the actor there, so this spot dims.
  const isRidingInTake = !!myActor && !!props.takeAttachments?.riderIds.has(myActor.id);

  const myStrategy = () => {
    if (!myActor) return NpcPointStrategy;
    if (myActor.id === "player") return PlayerPointStrategy;
    return ActorPointStrategy;
  };

  const onClick = (event: MouseEvent) => {
    if (disabled) return;
    //setAnchorEl(event.currentTarget);
    //setAnchorEl(event.currentTarget as Element);
    //setOpenPopover(true);
  };

  const onActorClick = (actor: Actor) => {
    setOpenPopover(false);

    addActorToSelectedTake(
      take,
      actor.id,
      positionId,
      myActor?.id,
      positionDirection
    );
  };

  const onActorDelete = () => {
    if (!take) return;
    if (!myActor) return;
    setOpenPopover(false);
    removeActorFromSelectedTake(take, myActor.id);
  };

  const options = actors.map((actor) => {
    return (
      <NpcPopoverButton
        key={actor.id}
        actor={actor}
        onClick={() => onActorClick(actor)}
        checked={actor.id === myActor?.id}
      />
    );
  });

  const deleteButton = (
    <div
      onClick={onActorDelete}
      className={classNames({
        " w-full flex cursor-pointer border-t-2 border-solid border-creator-primary-disabled pt-[8px]":
          true,
      })}
    >
      <div
        className={classNames({
          "my-auto text-[32px] px-[8px] w-full rounded-[5px] flex h-[42px]":
            true,
          "text-creator-error hover:bg-interaction-primary-selected":
            myActor !== undefined,
          "text-creator-primary-disabled": myActor === undefined,
        })}
      >
        <DeleteOutlineOutlinedIcon
          className={"my-auto"}
          color="inherit"
          fontSize="inherit"
        />
        <span className={"my-auto ml-[8px] text-[16px] font-medium"}>
          Delete
        </span>
      </div>
    </div>
  );

  const popoverContent = disabled ? undefined : (
    <>
      {options}
      {deleteButton}
    </>
  );

  return (
    <>
      <InteractionPointPreview
        animateFrom={props.animateFrom}
        strategy={myStrategy()}
        x={x}
        y={y}
        actor={myActor}
        markerBorderColor={takeColor}
        markerBackgroundColor={myActor?.id === "player" ? takeColor : undefined}
        directionDegrees={positionDirection?.x}
        directionColor={takeColor}
        disabled={disabled}
        iconClassName={
          sequencePlacement?.holdsProp || (myActor && props.takeAttachments?.holderIds.has(myActor.id))
            ? "sequence-under-attachment"
            : isRidingInTake
              ? "take-attachment-home"
              : sequenceUncertainClassName(sequencePlacement)
        }
        badgeContent={
          isRidingInTake ? <LinkIcon fontSize="inherit" /> : sequenceStateDot(sequencePlacement)
        }
        badgeClassName={
          isRidingInTake ? "take-attachment-home-badge" : sequenceStateDotClassName(sequencePlacement)
        }
        popoverContent={
          isSequenceState ? undefined : (
            <NpcPointPreviewPopover
              selectedActor={myActor}
              onActorClick={onActorClick}
              onDelete={onActorDelete}
              onAddNewActor={() => openQuickActorModal(positionId)}
            />
          )
        }
      />
    </>
  );
}
